import { FuelButton, FuelTypography, FuelIcon } from '@frete.com/fuel-react'
import { useState, useRef } from 'react'
import { DataTable, UserListingSkeleton, ModalConfirmation } from '~components'
import * as Styled from './styled'
import { useRequest } from 'ahooks'
import { Users } from '~services'
import { CompanyUserData, CompanyUsers } from '~services/interfaces'
import { format, isYesterday, isToday } from 'date-fns'
import { useGlobals, useAmplitude, useGetProduct } from '~hooks'
import { RegisterUser } from '~containers'
import { Dashboard, Filters } from './sections'
import useToast from '~hooks/useToast'
import { useNavigate } from 'react-router-dom'
import { Text } from '../../constants/text'
import { useTranslator } from '~hooks'
import { AmplitudeEvent } from '~constants/amplitude'
import { ForwardRefProps } from './sections/Filters/interfaces'
import { formattedPhone } from '~utils/formattedPhone'
import { useCombos } from '~hooks/useCombos'
import PurchaseExtraUsers from '~containers/PurchaseExtraUsers'

const initialValues = {
  name: '',
  email: '',
  cpf: '',
  confirmation_email: '',
  password: '',
  hasValidEmail: null,
  password_confirmation: '',
  phone: { number: '', type: null },
  permissionId: 2,
  sectorId: null,
}

function UserListing() {
  const filterRef = useRef<null | ForwardRefProps>(null)
  const [users, setUsers] = useState<null | CompanyUsers>(null)
  const [usersSearch, setUsersSearch] = useState<null | [] | CompanyUserData[]>(null)
  const { user } = useGlobals()
  const { getProduct } = useGetProduct(user)
  const [open, setOpen] = useState(false)
  const [usePurchaseUsers, setPurchaseUsers] = useState(false)
  const [userRegisterInitialValues, setUserRegisterInitialValues] = useState<CompanyUserData>(initialValues)
  const [isOpenDeleteteModal, setOpenDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState<CompanyUserData>(null)
  const [deletedUser, setDeletedUser] = useState('')
  const { sendAmplitude } = useAmplitude()
  const openToast = useToast()
  const navigate = useNavigate()
  const { translate } = useTranslator()

  const { Complete } = useCombos()

  const columns = [
    { field: 'name', name: 'nome', fontWeight: 700 },
    { field: 'email', name: 'e-mail' },
    { field: 'formattedPhone', name: 'telefone' },
    { field: 'accessLevel', name: 'nível de acesso' },
    { field: 'formattedLastAccess', name: 'último acesso' },
  ]
  const actions = [
    { name: 'Editar informações', icon: 'edit', onClick: editUser },
    {
      name: 'Excluir usuário',
      icon: 'delete',
      onClick: handleOpenDeleteModal,
      id: user?.data?.id,
      hide: user?.data?.typeDescription === 'Simples' || user?.data?.typeDescription === 'Colaborador',
      hideOnlyWhenMatch: user?.data?.typeDescription === 'Administrador',
    },
  ]

  function handleClose() {
    sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_CLICK_CLOSE_CREATE_USER, {
      user_id: user?.data?.id,
      company_id: user?.data?.company.id,
      type: 'editar-usuario',
    })
    setOpen(false)
    setUserRegisterInitialValues(initialValues)
  }

  function handleOpenRegisterModal(user?: CompanyUserData) {
    if (user) setUserRegisterInitialValues({ ...user, password: '', password_confirmation: '' })

    setOpen(true)
  }

  const primaryButton = {
    onClick: handleDeleteUser,
    text: 'Excluir usuário',
  }

  const secondaryButton = {
    onClick: handleCloseDeleteModal,
    text: 'Cancelar',
  }

  const {
    runAsync: getUsers,
    loading,
    data,
  } = useRequest(Users.getUsers, {
    onError: () => openToast('error', 'Algo deu errado!'),
    onSuccess: (response) => {
      setUsersSearch(null)
      setUsers(parseUsersResponse(response))
    },
  })

  const { runAsync: deleteUser, loading: loadingDelete } = useRequest(Users.deleteUser, {
    onSuccess: handleSuccessDelete,
    onError: handleErrorDelete,
    manual: true,
  })

  function handleSuccessDelete() {
    openToast('success', `O acesso ${deletedUser ?? `de ${deletedUser}`} foi excluído.`)
    void getUsers()
  }

  function handleErrorDelete() {
    handleCloseDeleteModal()
    openToast('error', 'Algo deu errado!')
  }

  function parseUsersResponse(response: CompanyUsers) {
    response.data = response.data.map((user) => {
      user.formattedPhone = user.phone?.number ? formattedPhone(user.phone?.number) : null

      user.accessLevel =
        user.permissionId === 1 ? 'Administrador' : user.permissionId === 2 ? 'Colaborador' : 'Colaborador simples'

      if (!user?.cpf) {
        user.cpf = ''
      }

      if (user.lastAccess) {
        const isDateToday = isToday(new Date(user.lastAccess))
        const isDateYesterday = isYesterday(new Date(user.lastAccess))

        if (isDateToday || isDateYesterday) {
          user.formattedLastAccess = isDateToday
            ? format(new Date(user.lastAccess), `'Hoje às' HH:mm`)
            : format(new Date(user.lastAccess), `'Ontem às' HH:mm`)
        } else {
          user.formattedLastAccess = format(new Date(user.lastAccess), `dd/MM/yyyy 'às' HH:mm`)
        }
      }

      return user
    })

    return response
  }

  function createUser() {
    if (users?.meta?.consumed >= users?.meta?.limit && !Complete.isComplete(user.data.company.combo.uuid)) {
      sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_USERS_LIMIT_REACHED, {
        user_id: user?.data?.id?.toString(),
        company_id: user?.data?.company.id,
        message: 'Tentativa de incluir novo usuário e é redirecionado para "Ver outros planos"',
        type: 'limite-usuarios-atingido',
      })

      navigate('/novos-planos')
      return
    }
    handleOpenRegisterModal()

    sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_BUTTON_CREATE, {
      company_id: user?.data?.company?.id,
      type: 'clique-cadastrar-usuário',
    })
  }

  function editUser(companyUser: CompanyUserData) {
    if (!companyUser.phone || !companyUser.phone.number) companyUser.phone = { number: '', type: null }

    handleOpenRegisterModal(companyUser)
    sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_CLICK_EDIT_MENU_KEBAB, {
      company_id: user?.data?.company?.id,
      user_id: user?.data?.id,
      type: 'editar-usuario-menu-kebab',
    })
  }

  async function handleDeleteUser() {
    if (loadingDelete) return

    await deleteUser(userToDelete?.id)
    setDeletedUser(userToDelete.name)
    handleCloseDeleteModal()

    sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_DELETE_USER, {
      company_id: user?.data?.company?.id,
      user_id: userToDelete?.id,
      type: 'deletar-usuario',
    })
  }

  function handleOpenDeleteModal(row: object) {
    setUserToDelete(row as CompanyUserData)
    setOpenDeleteModal(true)
    sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_CLICK_DELETE_MENU_KEBAB, {
      company_id: user?.data?.company?.id,
      user_id: user?.data?.id,
      type: 'deleta-usuario-menu-kebab',
    })
  }

  function handleCloseDeleteModal() {
    setOpenDeleteModal(false)
    setUserToDelete(null)
  }

  function openModalPurchaseUsers() {
    sendAmplitude(AmplitudeEvent.USER_MANAGEMENT_CLICK_PURCHASE_USERS, {
      company_id: user?.data?.company?.id,
      message: 'Click no botão de comprar usuários',
      type: 'user_management_purchase_users',
    })
    handleTogglePurchaseUsers()
  }

  function handleCloseClosePurchaseUsers() {
    handleTogglePurchaseUsers()
  }

  async function handleSuccessPurchaseExtraUsers() {
    handleTogglePurchaseUsers()
    await getUsers()
  }

  function handleTogglePurchaseUsers() {
    setPurchaseUsers(!usePurchaseUsers)
  }

  const userProduct = user?.data?.company?.combo?.products?.find((item) => item.slug === 'user')
  // const hasRangePrice = userProduct?.rangePrice?.length > 0
  // const hasReachedUsersLimit = data?.meta?.available === 0
  // const isComboComplete = user?.data && Complete.isComplete(user.data.company.combo.uuid)
  const showModalPurchaseUsers = false

  return (
    <>
      <div className="portal-filter-user" />
      <div className="portal-user-plans-success" />
      <PurchaseExtraUsers
        open={usePurchaseUsers}
        rangePrice={userProduct?.rangePrice}
        renewDay={user?.data?.company?.combo?.renew_day}
        companyId={user?.data?.company?.id}
        userId={user?.data?.id}
        onClose={handleCloseClosePurchaseUsers}
        onSuccess={handleSuccessPurchaseExtraUsers}
      />
      <RegisterUser
        open={open}
        onClose={handleClose}
        initialValues={userRegisterInitialValues}
        onSuccess={getUsers}
        isComboComplete={Complete.isNewest(user?.data?.company?.combo?.uuid)}
      />
      {loading && <UserListingSkeleton />}
      {!loading && (
        <Styled.Container>
          <Styled.Header>
            <FuelTypography tag="h1" size="heading-md" weight="bold">
              {translate(Text.ACCESS_MANAGEMENT)}
            </FuelTypography>
            <Styled.Subtitle>
              <FuelTypography tag="h2" size="body-md" weight="medium" color="neutral-default">
                {translate(Text.ORGANIZE_CUSTOMIZE)}
              </FuelTypography>
            </Styled.Subtitle>
          </Styled.Header>
          <Dashboard
            isComboComplete={Complete.isNewest(user?.data?.company?.combo?.uuid)}
            comboName={user?.data?.company?.combo?.nickname || user?.data?.company?.combo?.name}
            consumed={data?.meta?.consumed}
            initial={data?.meta?.initial}
            extras={data?.meta?.extras}
            limit={data?.meta?.limit}
            getProduct={getProduct}
          />

          <Filters
            ref={filterRef}
            usersRef={users?.data}
            handleSearch={(users) => {
              setUsersSearch(users)
            }}
          />

          <Styled.Body>
            <DataTable
              mobileColumns={['name', 'email']}
              columns={columns}
              rows={Array.isArray(usersSearch) ? usersSearch : users?.data || []}
              actions={actions}
              showActionOnlyWhenMatch={user?.data?.typeDescription === 'Colaborador'}
              comparisonProp="email"
              comparisonValue={user?.data?.email}
              currentUserType={user?.data?.typeDescription}
            />
          </Styled.Body>

          {usersSearch?.length === 0 && (
            <Styled.SearchResult>
              <FuelIcon name="search" size="giant" />
              <FuelTypography tag="h2" size="body-md" weight="bold">
                Nenhum resultado foi encontrado
              </FuelTypography>
              <FuelTypography tag="h2" size="body-md" weight="medium" color="neutral-default">
                Não encontramos nenhum resultado para a pesquisa realizada
              </FuelTypography>
              <FuelButton
                variant="tertiary"
                size="large"
                onClick={() => {
                  filterRef?.current?.getFormValues()
                }}
              >
                Refazer busca
              </FuelButton>
            </Styled.SearchResult>
          )}

          {user?.data?.typeDescription === 'Administrador' && (
            <Styled.Footer>
              {showModalPurchaseUsers ? (
                <FuelButton onClick={openModalPurchaseUsers} trailingIcon="plus">
                  {translate(Text.ORDER_USERS)}
                </FuelButton>
              ) : (
                <FuelButton onClick={createUser} trailingIcon="plus">
                  {translate(Text.REGISTER_USER)}
                </FuelButton>
              )}
            </Styled.Footer>
          )}
        </Styled.Container>
      )}
      <ModalConfirmation
        title={translate(Text.TITLE_DELETE_USER)}
        open={isOpenDeleteteModal}
        onClose={handleCloseDeleteModal}
        buttons={[secondaryButton, primaryButton]}
        isBackdrop={true}
        width="578px"
        portalName="modal-confirmation"
      >
        {translate(Text.DO_WANT_DELETE, { name: userToDelete?.name })}
      </ModalConfirmation>
    </>
  )
}

export default UserListing
